<template>
  <div class="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full space-y-8">
      <div>
        <h2 class="mt-6 text-center text-3xl font-bold text-tiye-dark">Reset Password</h2>
        <p class="mt-2 text-center text-sm text-tiye-dark/70">
          Enter the email on your Tiye account. We'll send a 6-digit code. The new password works for both Tiye and Move.
        </p>
      </div>

      <form v-if="!showResetForm" class="mt-8 space-y-6" @submit.prevent="sendResetCode">
        <div>
          <input
            id="email"
            v-model="email"
            type="email"
            required
            autocomplete="email"
            class="appearance-none rounded-lg relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-500 text-tiye-dark focus:outline-none focus:ring-2 focus:ring-tiye-green focus:border-tiye-green sm:text-sm"
            placeholder="Email address"
          />
        </div>

        <button
          type="submit"
          class="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-xl text-white bg-tiye-green hover:bg-tiye-dark transition-colors disabled:opacity-60"
          :disabled="loading"
        >
          {{ loading ? 'Sending...' : 'Send Reset Code' }}
        </button>
      </form>

      <form v-else class="mt-8 space-y-6" @submit.prevent="resetPassword">
        <div class="space-y-4">
          <input
            v-model="token"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            required
            maxlength="8"
            class="appearance-none rounded-lg block w-full px-4 py-3 border border-gray-300 text-tiye-dark tracking-[0.3em] text-center text-lg focus:ring-2 focus:ring-tiye-green"
            placeholder="6-digit code"
          />
          <input
            v-model="password"
            type="password"
            required
            minlength="8"
            autocomplete="new-password"
            class="appearance-none rounded-lg block w-full px-4 py-3 border border-gray-300 text-tiye-dark focus:ring-2 focus:ring-tiye-green"
            placeholder="New password (min 8 characters)"
          />
          <input
            v-model="passwordConfirmation"
            type="password"
            required
            minlength="8"
            autocomplete="new-password"
            class="appearance-none rounded-lg block w-full px-4 py-3 border border-gray-300 text-tiye-dark focus:ring-2 focus:ring-tiye-green"
            placeholder="Confirm new password"
          />
        </div>

        <button
          type="submit"
          class="w-full py-3 px-4 bg-tiye-green text-white rounded-xl font-medium hover:bg-tiye-dark transition-colors disabled:opacity-60"
          :disabled="loading"
        >
          {{ loading ? 'Resetting...' : 'Reset Password' }}
        </button>

        <button
          type="button"
          class="w-full text-sm font-medium text-tiye-green hover:text-tiye-dark disabled:opacity-60"
          :disabled="loading"
          @click="sendResetCode"
        >
          Resend code
        </button>
      </form>

      <div v-if="error" class="rounded-md bg-red-50 p-4">
        <p class="text-sm font-medium text-red-800">{{ error }}</p>
      </div>

      <div v-if="success" class="rounded-md bg-green-50 p-4">
        <p class="text-sm font-medium text-green-800">{{ success }}</p>
      </div>

      <div class="text-center">
        <router-link to="/login" class="font-medium text-tiye-green hover:text-tiye-dark transition-colors">
          Back to Login
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { moveApi } from '../services/moveApi'

const router = useRouter()
const email = ref('')
const token = ref('')
const password = ref('')
const passwordConfirmation = ref('')
const loading = ref(false)
const error = ref('')
const success = ref('')
const showResetForm = ref(false)

const sendResetCode = async () => {
  loading.value = true
  error.value = ''
  success.value = ''
  try {
    const { data } = await moveApi.requestPasswordReset(email.value.trim())
    if (data?.success === false) {
      error.value = data.message || 'Failed to send reset code.'
      return
    }
    success.value = data?.message || 'If an account exists, a reset code has been sent to your email. Check your inbox and spam folder.'
    showResetForm.value = true
  } catch (err) {
    error.value = err.response?.data?.message || 'Failed to send reset code. Please try again.'
  } finally {
    loading.value = false
  }
}

const resetPassword = async () => {
  if (password.value !== passwordConfirmation.value) {
    error.value = 'Passwords do not match.'
    return
  }
  loading.value = true
  error.value = ''
  try {
    await moveApi.resetPassword({
      email: email.value.trim(),
      token: token.value.replace(/\s+/g, ''),
      password: password.value,
      password_confirmation: passwordConfirmation.value
    })
    success.value = 'Password reset successfully. You can now sign in.'
    setTimeout(() => router.push('/login'), 1500)
  } catch (err) {
    error.value = err.response?.data?.message || 'Failed to reset password.'
  } finally {
    loading.value = false
  }
}
</script>
